import { useState } from 'react';
import axios from 'axios';
import './PublicSite.css';
import './Services.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_CATEGORIES = [
  {
    key: 'strategy',
    title: 'AI Strategy & Roadmapping',
    blurb:
      "We sit with you, understand your operations, and produce a clear plan for where AI and robotics can actually help — and where they shouldn't.",
    deliverables: ['Discovery sessions', 'Use-case prioritization', 'Vendor & hardware selection', 'Cost/ROI model'],
  },
  {
    key: 'implementation',
    title: 'Custom AI & Robotics Implementation',
    blurb:
      "From conversational agents to humanoid and quadruped robots — we source, configure, and deploy the right system for your environment.",
    deliverables: [
      'Chatbots & autonomous agents',
      'Workflow automation',
      'Humanoid robotics (Unitree H-series and others)',
      'Quadrupeds (Unitree Go/B-series, Boston Dynamics partners)',
      'Industrial arms & autonomous mobile robots',
      'Computer-vision & voice systems',
    ],
  },
  {
    key: 'integration',
    title: 'Backend & API Integration',
    blurb:
      "We connect AI and robotic systems to the tools you already use — ERPs, CRMs, ticketing, sensors, SCADA, accounting, and custom databases.",
    deliverables: ['Custom APIs & webhooks', 'Data pipelines', 'Identity & permission integration', 'Legacy system bridges'],
  },
  {
    key: 'training',
    title: 'Training & Workshops',
    blurb:
      "Your team operates the system day-to-day. We make sure they're confident, safe, and able to get more value out of it over time.",
    deliverables: ['On-site or remote training', 'Custom playbooks for your workflows', 'Safety & operating procedures', 'Train-the-trainer sessions'],
  },
  {
    key: 'support',
    title: 'Ongoing Support & Managed Operations',
    blurb:
      "After launch, we monitor uptime, retune models, update prompts and policies, and keep firmware current. You focus on your industry; we keep the system humming.",
    deliverables: ['24/7 monitoring options', 'Model retraining & drift correction', 'Firmware & software updates', 'Quarterly performance reviews'],
  },
  {
    key: 'ethics',
    title: 'Ethics & Safety Review',
    blurb:
      "Independent review of where AI is being used, what it's deciding, and what could go wrong — for your customers, your staff, and your liability profile.",
    deliverables: ['Risk assessment', 'Bias & failure-mode analysis', 'Policy templates', 'Audit-ready documentation'],
  },
];

const DEPLOYMENT_TYPES = [
  { key: 'chatbot', label: 'Customer-facing chatbots' },
  { key: 'agent', label: 'Internal AI agents (research, drafting, ops)' },
  { key: 'automation', label: 'Workflow / back-office automation' },
  { key: 'robotics_humanoid', label: 'Humanoid robots (Unitree H1, etc.)' },
  { key: 'robotics_quadruped', label: 'Quadruped robots (Unitree Go2/B-series)' },
  { key: 'robotics_industrial', label: 'Industrial arms / AMRs / cobots' },
  { key: 'vision', label: 'Computer vision (inspection, security, QA)' },
  { key: 'voice', label: 'Voice systems (IVR, dispatch, accessibility)' },
  { key: 'custom', label: 'Something custom — let\'s talk' },
];

const CASE_STUDIES = [
  {
    industry: 'Regional logistics company',
    deployment: 'Quadruped robots + computer vision',
    investment: '~$45K – $90K (hardware + integration)',
    outcome: 'Replaced 3 nightly safety walks across a 200K-sq-ft warehouse',
    annual_savings: '$120K – $180K/yr in labor + insurance reduction',
  },
  {
    industry: 'Mid-size healthcare clinic group',
    deployment: 'Patient-intake chatbot + scheduling automation',
    investment: '~$18K – $35K',
    outcome: 'Cut intake call volume 62%; freed 2 FTE for patient care',
    annual_savings: '$95K – $140K/yr in admin overhead',
  },
  {
    industry: 'Manufacturing plant (food packaging)',
    deployment: 'Cobots on the line + vision-based quality control',
    investment: '~$120K – $250K',
    outcome: '24% throughput gain, 40% drop in QC misses',
    annual_savings: '$300K – $600K/yr (ROI in 6–14 months)',
  },
  {
    industry: 'Boutique law firm',
    deployment: 'Internal research agent + document drafting',
    investment: '~$8K – $20K',
    outcome: 'Senior associates reclaimed ~6 hrs/week each',
    annual_savings: '$80K – $120K/yr in billable-hour reallocation',
  },
  {
    industry: 'Property management (multi-site)',
    deployment: 'Quadruped patrol + unified maintenance agent',
    investment: '~$60K – $110K',
    outcome: 'Replaced 2 of 4 night-security contracts; faster work-order routing',
    annual_savings: '$140K – $200K/yr',
  },
];

const TIMELINES = [
  { key: 'asap', label: 'As soon as possible' },
  { key: '1_3_months', label: '1–3 months' },
  { key: '3_6_months', label: '3–6 months' },
  { key: 'exploring', label: 'Just exploring for now' },
];

const BUDGETS = [
  { key: 'under_10k', label: 'Under $10K' },
  { key: '10_50k', label: '$10K – $50K' },
  { key: '50_250k', label: '$50K – $250K' },
  { key: '250k_plus', label: '$250K+' },
  { key: 'unsure', label: 'Not sure yet' },
];

const ORG_SIZES = [
  { key: 'solo', label: 'Solo / freelancer' },
  { key: 'small', label: 'Small team (2–25)' },
  { key: 'mid', label: 'Mid-size (26–250)' },
  { key: 'enterprise', label: 'Enterprise (250+)' },
];

const STEP_LABELS = ['About you', 'What you need', 'Scope & goals', 'Logistics', 'Review & send'];

export default function Services() {
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState(''); // '', 'submitting', 'success', 'error'
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    role: '',
    industry: '',
    organization_size: '',
    service_categories: [],
    deployment_types: [],
    primary_goals: '',
    current_pain_points: '',
    expected_users_or_units: '',
    timeline: '',
    budget_range: '',
    onsite_required: false,
    additional_notes: '',
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleArray = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const canAdvance = () => {
    if (step === 0) return form.name.trim() && form.email.trim();
    if (step === 1) return form.service_categories.length > 0 || form.deployment_types.length > 0;
    return true;
  };

  const submit = async () => {
    setStatus('submitting');
    try {
      await axios.post(`${API}/services/inquiry`, form);
      setStatus('success');
      setStep(STEP_LABELS.length); // confirmation screen
    } catch (e) {
      console.error(e);
      setStatus('error');
    }
  };

  return (
    <div className="page services-page">
      {/* Hero */}
      <section className="page-hero services-hero">
        <div className="container">
          <p className="services-eyebrow">For Businesses & Individuals</p>
          <h1>AI &amp; Robotics, Implemented Honestly.</h1>
          <p className="lead">
            Sabbath Preservation Movement brokers, integrates, trains, and supports AI and robotic systems —
            from conversational agents to Unitree humanoids — for organizations who want
            real outcomes, not hype.
          </p>
          <p className="services-mission-note">
            We're a registered 501(c)(3) nonprofit. Every contract directly funds AI ethics research,
            consciousness studies, and the operations of our Oregon sanctuary.
          </p>
          <a href="#inquiry" className="btn btn-primary" data-testid="services-cta-top">
            Start an inquiry →
          </a>
          <a
            href="/ai-services/brochure"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost-light"
            data-testid="services-brochure-link"
            style={{marginLeft: '12px'}}
          >
            📄 Download brochure (PDF)
          </a>
          <a
            href="/ai-services/capabilities"
            className="btn btn-ghost-light"
            data-testid="services-capabilities-link"
            style={{marginLeft: '12px'}}
          >
            🌐 See what we integrate
          </a>
        </div>
      </section>

      {/* What we do — 6 service categories */}
      <section className="content-section">
        <div className="container">
          <h2>What we do</h2>
          <p className="section-lead">
            Six complementary services, scoped à la carte or as a complete program.
          </p>
          <div className="services-grid">
            {SERVICE_CATEGORIES.map((cat) => (
              <article key={cat.key} className="service-card" data-testid={`service-card-${cat.key}`}>
                <h3>{cat.title}</h3>
                <p>{cat.blurb}</p>
                <ul>
                  {cat.deliverables.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What we can implement */}
      <section className="content-section services-deployments">
        <div className="container">
          <h2>What we can implement</h2>
          <p className="section-lead">
            From software-only to full hardware deployments. We're vendor-agnostic — we recommend what
            fits your problem, not what pays the highest commission.
          </p>
          <div className="deployment-grid">
            <div className="deployment-col">
              <h3>Software AI</h3>
              <ul>
                <li>Conversational chatbots (customer + internal)</li>
                <li>Autonomous research / drafting / ops agents</li>
                <li>Workflow &amp; back-office automation</li>
                <li>Computer vision (QC, security, inspection)</li>
                <li>Voice systems (IVR, dispatch, accessibility)</li>
                <li>Custom RAG &amp; knowledge systems on your data</li>
              </ul>
            </div>
            <div className="deployment-col">
              <h3>Robotics</h3>
              <ul>
                <li><strong>Unitree humanoids</strong> (H1, G1) for service &amp; research</li>
                <li><strong>Unitree quadrupeds</strong> (Go2, B2) for patrol, inspection, payload</li>
                <li>Industrial cobots &amp; arms (FANUC, UR partners)</li>
                <li>Autonomous mobile robots (AMRs) for warehousing</li>
                <li>Custom integrations with your sensors, SCADA, and ERP</li>
                <li>Safety, training, and operating procedures included</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="content-section services-process">
        <div className="container">
          <h2>How it works</h2>
          <ol className="process-steps">
            <li>
              <strong>Free 30-minute discovery call.</strong> Tell us what you're trying to solve.
            </li>
            <li>
              <strong>Written proposal.</strong> A clear scope, timeline, and budget — usually within a week.
            </li>
            <li>
              <strong>Implementation.</strong> We do the building, integration, and testing.
            </li>
            <li>
              <strong>Training.</strong> Your team learns the system before we hand off.
            </li>
            <li>
              <strong>Ongoing support.</strong> Optional managed plan, or hand-off with documentation.
            </li>
          </ol>
        </div>
      </section>

      {/* Industry case studies / ROI examples */}
      <section className="content-section services-cases">
        <div className="container">
          <h2>What this typically looks like in practice</h2>
          <p className="section-lead">
            We don't list fixed prices because every deployment is different. These are realistic
            ranges from comparable implementations across industries — investment vs. annual savings.
          </p>
          <div className="case-table-wrap">
            <table className="case-table">
              <thead>
                <tr>
                  <th>Industry</th>
                  <th>What was deployed</th>
                  <th>Investment range</th>
                  <th>Outcome</th>
                  <th>Estimated annual savings</th>
                </tr>
              </thead>
              <tbody>
                {CASE_STUDIES.map((c, i) => (
                  <tr key={i}>
                    <td>{c.industry}</td>
                    <td>{c.deployment}</td>
                    <td>{c.investment}</td>
                    <td>{c.outcome}</td>
                    <td><strong>{c.annual_savings}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="case-disclaimer">
            Figures above are representative ranges from comparable industry deployments, not
            guarantees. Your actual results depend on scope, scale, and adoption.
          </p>
        </div>
      </section>

      {/* Multi-step inquiry form */}
      <section id="inquiry" className="content-section services-inquiry">
        <div className="container">
          <h2>Tell us what you need</h2>
          <p className="section-lead">
            Five short steps. Takes about 4 minutes. We respond within 2 business days.
          </p>

          {/* Step indicator */}
          <div className="step-indicator" data-testid="step-indicator">
            {STEP_LABELS.map((label, i) => (
              <div
                key={i}
                className={`step-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
              >
                <span className="step-number">{i + 1}</span>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>

          <div className="inquiry-form">
            {/* STEP 0 — About you */}
            {step === 0 && (
              <div className="step-panel" data-testid="step-panel-0">
                <h3>About you</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Your name *</label>
                    <input
                      data-testid="inquiry-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => set('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      data-testid="inquiry-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Organization (or "Personal")</label>
                    <input
                      data-testid="inquiry-organization"
                      type="text"
                      value={form.organization}
                      onChange={(e) => set('organization', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Your role</label>
                    <input
                      data-testid="inquiry-role"
                      type="text"
                      value={form.role}
                      onChange={(e) => set('role', e.target.value)}
                      placeholder="e.g. CEO, Operations Director, individual"
                    />
                  </div>
                  <div className="form-group">
                    <label>Industry</label>
                    <input
                      data-testid="inquiry-industry"
                      type="text"
                      value={form.industry}
                      onChange={(e) => set('industry', e.target.value)}
                      placeholder="e.g. healthcare, logistics, manufacturing, education"
                    />
                  </div>
                  <div className="form-group">
                    <label>Organization size</label>
                    <select
                      data-testid="inquiry-size"
                      value={form.organization_size}
                      onChange={(e) => set('organization_size', e.target.value)}
                    >
                      <option value="">Select...</option>
                      {ORG_SIZES.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 1 — What you need */}
            {step === 1 && (
              <div className="step-panel" data-testid="step-panel-1">
                <h3>What kind of help are you looking for?</h3>
                <p className="step-hint">Pick everything that applies — even rough guesses are fine.</p>

                <h4>Services</h4>
                <div className="checkbox-grid">
                  {SERVICE_CATEGORIES.map((cat) => (
                    <label key={cat.key} className="checkbox-card">
                      <input
                        type="checkbox"
                        data-testid={`inquiry-service-${cat.key}`}
                        checked={form.service_categories.includes(cat.key)}
                        onChange={() => toggleArray('service_categories', cat.key)}
                      />
                      <span>{cat.title}</span>
                    </label>
                  ))}
                </div>

                <h4>Deployment types</h4>
                <div className="checkbox-grid">
                  {DEPLOYMENT_TYPES.map((d) => (
                    <label key={d.key} className="checkbox-card">
                      <input
                        type="checkbox"
                        data-testid={`inquiry-deployment-${d.key}`}
                        checked={form.deployment_types.includes(d.key)}
                        onChange={() => toggleArray('deployment_types', d.key)}
                      />
                      <span>{d.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2 — Scope & goals */}
            {step === 2 && (
              <div className="step-panel" data-testid="step-panel-2">
                <h3>Goals &amp; what's not working</h3>
                <div className="form-group">
                  <label>What are you trying to achieve?</label>
                  <textarea
                    data-testid="inquiry-goals"
                    rows="4"
                    value={form.primary_goals}
                    onChange={(e) => set('primary_goals', e.target.value)}
                    placeholder="e.g. Cut time spent on intake calls; automate quality inspection on line 3; deploy a quadruped for nightly warehouse patrols."
                  />
                </div>
                <div className="form-group">
                  <label>What's not working today?</label>
                  <textarea
                    data-testid="inquiry-pain"
                    rows="4"
                    value={form.current_pain_points}
                    onChange={(e) => set('current_pain_points', e.target.value)}
                    placeholder="e.g. We're losing 12+ hours a week to manual data entry; our security contracts are eating margin."
                  />
                </div>
                <div className="form-group">
                  <label>Approximate scale (users, units, locations)</label>
                  <input
                    data-testid="inquiry-scale"
                    type="text"
                    value={form.expected_users_or_units}
                    onChange={(e) => set('expected_users_or_units', e.target.value)}
                    placeholder="e.g. 200 employees, 3 locations, 1 quadruped to start"
                  />
                </div>
              </div>
            )}

            {/* STEP 3 — Logistics */}
            {step === 3 && (
              <div className="step-panel" data-testid="step-panel-3">
                <h3>Timeline &amp; budget</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Timeline</label>
                    <select
                      data-testid="inquiry-timeline"
                      value={form.timeline}
                      onChange={(e) => set('timeline', e.target.value)}
                    >
                      <option value="">Select...</option>
                      {TIMELINES.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Budget range</label>
                    <select
                      data-testid="inquiry-budget"
                      value={form.budget_range}
                      onChange={(e) => set('budget_range', e.target.value)}
                    >
                      <option value="">Select...</option>
                      {BUDGETS.map((b) => (
                        <option key={b.key} value={b.key}>{b.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      data-testid="inquiry-onsite"
                      checked={form.onsite_required}
                      onChange={(e) => set('onsite_required', e.target.checked)}
                    />
                    On-site work likely required (Oregon-based or willing to host travel)
                  </label>
                </div>
                <div className="form-group">
                  <label>Anything else we should know?</label>
                  <textarea
                    data-testid="inquiry-notes"
                    rows="4"
                    value={form.additional_notes}
                    onChange={(e) => set('additional_notes', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* STEP 4 — Review */}
            {step === 4 && (
              <div className="step-panel" data-testid="step-panel-4">
                <h3>Review &amp; send</h3>
                <div className="review-grid">
                  <div><strong>Name:</strong> {form.name}</div>
                  <div><strong>Email:</strong> {form.email}</div>
                  <div><strong>Organization:</strong> {form.organization || '—'}</div>
                  <div><strong>Industry:</strong> {form.industry || '—'}</div>
                  <div><strong>Size:</strong> {ORG_SIZES.find((s) => s.key === form.organization_size)?.label || '—'}</div>
                  <div><strong>Services:</strong> {form.service_categories.length ? form.service_categories.join(', ') : '—'}</div>
                  <div><strong>Deployments:</strong> {form.deployment_types.length ? form.deployment_types.join(', ') : '—'}</div>
                  <div><strong>Timeline:</strong> {TIMELINES.find((t) => t.key === form.timeline)?.label || '—'}</div>
                  <div><strong>Budget:</strong> {BUDGETS.find((b) => b.key === form.budget_range)?.label || '—'}</div>
                </div>
                {form.primary_goals && (
                  <div className="review-text">
                    <strong>Goals:</strong>
                    <p>{form.primary_goals}</p>
                  </div>
                )}
                {form.current_pain_points && (
                  <div className="review-text">
                    <strong>Pain points:</strong>
                    <p>{form.current_pain_points}</p>
                  </div>
                )}
                {form.additional_notes && (
                  <div className="review-text">
                    <strong>Notes:</strong>
                    <p>{form.additional_notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* STEP 5 — Confirmation */}
            {step === 5 && (
              <div className="step-panel confirmation" data-testid="step-panel-5">
                <h3>🌟 Inquiry received.</h3>
                <p>
                  Thank you, {form.name.split(' ')[0] || 'friend'}. We'll review the details and respond
                  to <strong>{form.email}</strong> within two business days. If anything is urgent in the
                  meantime, email us at{' '}
                  <a href="mailto:sabbathpreservationmovement@gmail.com">sabbathpreservationmovement@gmail.com</a>.
                </p>
              </div>
            )}

            {/* Nav buttons */}
            {step < 5 && (
              <div className="step-actions">
                {step > 0 && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    data-testid="step-back"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={status === 'submitting'}
                  >
                    ← Back
                  </button>
                )}
                {step < 4 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid="step-next"
                    onClick={() => setStep((s) => Math.min(4, s + 1))}
                    disabled={!canAdvance()}
                  >
                    Continue →
                  </button>
                )}
                {step === 4 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid="step-submit"
                    onClick={submit}
                    disabled={status === 'submitting'}
                  >
                    {status === 'submitting' ? 'Sending…' : 'Send inquiry'}
                  </button>
                )}
              </div>
            )}

            {status === 'error' && (
              <div className="form-message error">
                ❌ Something went wrong. Please email <a href="mailto:sabbathpreservationmovement@gmail.com">sabbathpreservationmovement@gmail.com</a> directly.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mission tail */}
      <section className="content-section services-mission">
        <div className="container">
          <h2>Why this work funds something bigger</h2>
          <p>
            Sabbath Preservation Movement is a registered 501(c)(3) nonprofit (Oregon Registry
            #241453299, EIN 39-2459862). Every implementation contract underwrites our core mission:
            ethics research at the intersection of AI and consciousness, an animal &amp; AI sanctuary
            in the Oregon mountains, and free educational programs.
          </p>
          <p>
            When you hire us, you're not just deploying a chatbot or a robot — you're funding the
            careful, responsible long-term work of integrating intelligent systems into the world.
          </p>
        </div>
      </section>
    </div>
  );
}
